import { useEffect, useRef, useState } from "preact/hooks";
import { buildAppClipUrl } from "@core/hooks/useAppClipUrl";
import { QrCodeGenerator } from "@core/components/QrCodeGenerator";
import { Spinner } from "@core/components/Elements";
import { getExperienceMode } from "@core/utils/experienceMode";
import getUserDevice from "@core/utils/getUserDevice";
import useWindowDimensions from "@core/hooks/useWindowDimensions";
import viewInArIcon from "@assets-shared/icons/view-in-ar-icon.svg";
import type { ModelData } from "../data/data";

interface ARButtonProps {
  model: ModelData;
  onLaunchAR: (model: ModelData) => void;
  onTrackLaunchClick?: (
    model: ModelData,
    options?: { transportType?: "beacon" }
  ) => void;
  isChecking: boolean;
  isCompatible: boolean;
  className?: string;
  qrCodeImageSrc?: string;
  variant?: "primary" | "outline";
}

export function ARButton({
  model,
  onLaunchAR,
  onTrackLaunchClick,
  isChecking,
  isCompatible,
  className = "",
  qrCodeImageSrc,
  variant = "primary",
}: ARButtonProps) {
  const [showQRPopup, setShowQRPopup] = useState(false);
  const [isIOSWarmupLoading, setIsIOSWarmupLoading] = useState(false);
  const [popupWidth, setPopupWidth] = useState(240);
  const { device, isMobile } = getUserDevice();
  const { width } = useWindowDimensions();
  const qrContainerRef = useRef<HTMLDivElement>(null);
  const buttonWrapperRef = useRef<HTMLDivElement>(null);
  const resolvedQrCodeImageSrc = model.qrCodePath ?? qrCodeImageSrc;

  const isDesktop = !isMobile;
  const isIOS = device === "iOS" || device === "AppClip";
  const qrSize = Math.max(120, popupWidth - 16);

  const handleClick = () => {
    if (isDesktop) {
      setShowQRPopup(true);
      return;
    }

    if (isIOS) {
      onTrackLaunchClick?.(model, { transportType: "beacon" });

      try {
        const u = new URL(window.location.href);
        if (model.urlHash) {
          u.hash = model.urlHash;
        } else {
          u.hash = model.id;
        }
        u.searchParams.set("fromAr", "1");
        window.history.pushState({}, "", u.toString());
      } catch {
        // no-op
      }

      const returnUrl = window.location.href;
      const isOnsite =
        window.location.pathname.startsWith("/onsite") ||
        getExperienceMode() === "onsite";
      const baseUrl = isOnsite
        ? `${window.location.origin}/onsite`
        : window.location.origin + window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      const appClipParams: Record<string, string> = {};
      const keysToForward = ["config"];
      for (const key of keysToForward) {
        const value = searchParams.get(key);
        if (value !== null) {
          appClipParams[key] = value;
        }
      }

      window.location.href = buildAppClipUrl(model.id, {
        returnUrl,
        baseUrl,
        params: appClipParams,
      });
      return;
    }

    if (isCompatible) {
      onLaunchAR(model);
    }
  };

  const qrUrl = (() => {
    try {
      const u = new URL(window.location.href);
      if (model.urlHash) {
        u.hash = model.urlHash;
      } else {
        u.hash = model.id;
      }
      return u.toString();
    } catch {
      return window.location.href;
    }
  })();

  useEffect(() => {
    if (isMobile || !showQRPopup) return;

    const handleOutsideClick = (event: PointerEvent) => {
      if (!qrContainerRef.current?.contains(event.target as Node)) {
        setShowQRPopup(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
    };
  }, [isMobile, showQRPopup]);

  useEffect(() => {
    if (!isIOS) {
      setIsIOSWarmupLoading(false);
      return;
    }

    setIsIOSWarmupLoading(true);
    const timeoutId = window.setTimeout(() => {
      setIsIOSWarmupLoading(false);
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isIOS]);

  useEffect(() => {
    if (isMobile) return;

    const measure = () => {
      const measuredWidth = buttonWrapperRef.current?.getBoundingClientRect().width;
      if (measuredWidth && measuredWidth > 0) {
        setPopupWidth(Math.max(240, Math.round(measuredWidth)));
      }
    };

    measure();
    window.addEventListener("resize", measure);

    return () => {
      window.removeEventListener("resize", measure);
    };
  }, [isMobile, width]);

  const isLoading = !isDesktop && ((isIOS && (isChecking || isIOSWarmupLoading)) || (!isIOS && isChecking));
  const isDisabled = !isDesktop && !isIOS && (isChecking || !isCompatible);

  const buttonStyle = variant === "outline"
    ? "bg-transparent border-2 border-black text-black hover:bg-black/5 active:bg-black/10"
    : "bg-[#FFDD15] border border-[#FFDD15] text-black hover:bg-[#f5d300] active:bg-[#f0cd00]";
  const disabledStyle = variant === "outline"
    ? "bg-transparent border-2 border-neutral-400 text-neutral-500 cursor-not-allowed opacity-60"
    : "bg-[#f7ea95] border border-[#f7ea95] text-black/50 cursor-not-allowed opacity-70";

  const containerWidth = variant === "outline" ? "w-[260px]" : "w-full sm:w-[220px]";

  return (
    <div
      ref={qrContainerRef}
      className={`relative ${containerWidth} h-[70px] ${className}`}
    >
      <div ref={buttonWrapperRef} className="w-full">
        {isLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <Spinner size="2md" variant="primary" />
          </div>
        ) : (
          <button
            onClick={handleClick}
            disabled={isDisabled}
            className={`w-full h-[70px] xh-full xpx-4 flex items-center justify-center gap-5 font-bold text-[18px] sm:text-[19px] leading-[1] tracking-[0.01em] transition-colors ${
              isDisabled ? disabledStyle : buttonStyle
            }`}
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <span>VIEW IN AR</span>
            <img
              src={viewInArIcon}
              alt=""
              aria-hidden="true"
              className="h-[30px] w-[18px] sm:h-[34px] sm:w-[20px] shrink-0 brightness-0"
            />
          </button>
        )}
      </div>

      {isDesktop && (
        <div
          className={`absolute bottom-full left-1/2 mb-10 z-[1001] -translate-x-1/2 transform drop-shadow-[0_18px_30px_rgba(0,0,0,0.24)] transition-all duration-150 ease-out ${
            showQRPopup
              ? "translate-y-0 opacity-100 pointer-events-auto"
              : "translate-y-5 opacity-0 pointer-events-none"
          }`}
        >
          <div
            className="bg-white rounded-none flex flex-col items-center pt-1 px-1 pb-5"
            style={{
              width: `${popupWidth}px`,
              boxSizing: "border-box",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            {resolvedQrCodeImageSrc ? (
              <div
                className="p-1 bg-white aspect-square flex items-center justify-center overflow-hidden"
              >
                <img
                  src={resolvedQrCodeImageSrc}
                  alt="QR code to view in AR"
                  className="block"
                  style={{ width: `${qrSize}px`, height: `${qrSize}px` }}
                />
              </div>
            ) : (
              <QrCodeGenerator value={qrUrl} fgColor="#000000" size={qrSize} />
            )}
            <p  className="text-black text-center mt-2"
              style={{
                fontSize: "18px",
                lineHeight: "1.2",
                letterSpacing: "0.35px",
              }}>
              Scan QR code
            </p>
          </div>
          <div
            className="absolute left-1/2 -translate-x-1/2 -bottom-[18px] w-0 h-0"
            style={{
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderTop: "18px solid white",
            }}
          />
        </div>
      )}
    </div>
  );
}
